import React from "react";
import { Printer, MoreHorizontal, Edit, Share2, User, Wallet, XCircle } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { StatusChip, type StatusTone } from "@/components/ui/status-chip";
import { useBillingUtils } from "@/hooks/useBillingUtils";
import { cn } from "@/lib/utils";
import type { Invoice } from "@/types";

interface InvoiceTableProps {
  invoices: Invoice[];
  selectedId?: string | null;
  onView: (invoice: Invoice) => void;
  onPrint: (invoice: Invoice) => void;
  onPayment?: (invoice: Invoice) => void;
  onEdit?: (invoice: Invoice) => void;
  onCancel?: (invoice: Invoice) => void;
  onViewPatient?: (patientId: string) => void;
  /** One patient's bills (the patient page): no Patient column, the invoice number leads the row */
  hidePatient?: boolean;
  /** Pay the row's due from the patient's advance balance; pass only while there is advance to use */
  onApplyAdvance?: (invoice: Invoice) => void;
  /** Clinic list: a divider row where the invoice date changes */
  groupByDate?: boolean;
}

function shareOnWhatsApp(invoice: Invoice, formatDate: (d: string) => string) {
  const amount = Number(invoice.total_amount).toLocaleString('en-IN');
  const statusText =
    invoice.payment_status === 'paid'    ? 'fully paid ✓' :
    invoice.payment_status === 'partial' ? `partial — ₹${Number(invoice.balance_due).toLocaleString('en-IN')} still due` :
                                           'pending payment';
  const lines = [
    `Dear ${invoice.patient_name},`,
    ``,
    `Your bill (${invoice.invoice_number}) dated ${formatDate(invoice.invoice_date)}`,
    `Amount: ₹${amount} — ${statusText}`,
    ``,
    `Thank you.`,
  ];
  const msg = encodeURIComponent(lines.join('\n'));
  const phone = invoice.patient_phone?.replace(/\D/g, '');
  const url = phone
    ? `https://wa.me/91${phone}?text=${msg}`
    : `https://wa.me/?text=${msg}`;
  window.open(url, '_blank', 'noopener,noreferrer');
}

// One grid for header and rows from lg up (landscape tablets too) so columns line up; below lg rows stack
const ROW_GRID = "lg:grid lg:grid-cols-[minmax(0,1.6fr)_8.5rem_6.5rem_5.5rem_7rem_7rem_8.5rem] lg:gap-3 lg:items-center";

const ROW_GRID_PATIENT = "lg:grid lg:grid-cols-[minmax(0,1fr)_6.5rem_5.5rem_7rem_7rem_15rem] lg:gap-3 lg:items-center";

const MODE_LABEL: Record<string, string> = {
  cash: "Cash", upi: "UPI", card: "Card", netbanking: "Net banking", cheque: "Cheque", advance: "Advance", other: "Other",
};
/** How a bill was paid ("UPI", "Cash, UPI"), from its payment records */
export function paidVia(invoice: Invoice): string {
  const modes = String((invoice as any).payment_modes ?? "").split(",").map(m => m.trim()).filter(Boolean);
  return modes.map(m => MODE_LABEL[m] ?? m).join(", ");
}

/** A divider label for an invoice date: Today, Yesterday, or the date */
function dayLabel(d: string, formatDate: (d: string) => string): string {
  const key = String(d).slice(0, 10);
  const today = new Date(); const yday = new Date(Date.now() - 86400000);
  const ymd = (x: Date) => `${x.getFullYear()}-${String(x.getMonth() + 1).padStart(2, "0")}-${String(x.getDate()).padStart(2, "0")}`;
  return key === ymd(today) ? "Today" : key === ymd(yday) ? "Yesterday" : formatDate(d);
}

export function invoiceStatus(invoice: Invoice): { tone: StatusTone; label: string } {
  if (invoice.payment_status === "cancelled") return { tone: "neutral", label: "Cancelled" };
  if (invoice.payment_status === "paid") return { tone: "success", label: "Paid" };
  if (invoice.payment_status === "partial") return { tone: "accent", label: "Partial" };
  return { tone: "warning", label: "Unpaid" };
}

/** Invoice list (clinic-wide, or one patient's): 32px header, 40px rows, right-aligned amounts, row → details panel. */
export default function InvoiceTable({
  invoices, selectedId, onView, onPrint, onPayment, onEdit, onCancel, onViewPatient, hidePatient, onApplyAdvance, groupByDate,
}: InvoiceTableProps) {
  const { formatCurrency, formatDate } = useBillingUtils();
  const grid = hidePatient ? ROW_GRID_PATIENT : ROW_GRID;

  return (
    <div role="table" aria-label="Invoices" className="rounded-lg border border-border bg-card overflow-hidden">
      <div role="row" className={cn(grid, "hidden h-8 px-3 border-b border-border bg-muted/40 text-2xs font-medium uppercase tracking-wide text-muted-foreground")}>
        {!hidePatient && <span role="columnheader">Patient</span>}
        <span role="columnheader">Invoice</span>
        <span role="columnheader">Date</span>
        <span role="columnheader">Status</span>
        <span role="columnheader" className="text-right">Amount</span>
        <span role="columnheader" className="text-right">Due</span>
        <span role="columnheader" className="text-right">Actions</span>
      </div>

      {invoices.map((invoice, i) => {
        const status = invoiceStatus(invoice);
        // Clinic list: a divider where the day changes (Today, Yesterday, then dates)
        const newDay = groupByDate && (i === 0 || String(invoices[i - 1].invoice_date).slice(0, 10) !== String(invoice.invoice_date).slice(0, 10));
        const cancelled = invoice.payment_status === "cancelled";
        const due = Number(invoice.balance_due) || 0;
        const canPay = !cancelled && due > 0 && onPayment;
        // IPD bills are built from the admission's charges — changed there (Reopen bill), not here
        const canEdit = onEdit && invoice.payment_status !== "paid" && !cancelled && !invoice.admission_id;

        return (
          <React.Fragment key={invoice.id}>
          {newDay && (
            <div role="row" aria-hidden className="flex items-center h-7 px-3 border-b border-border bg-muted/40 text-2xs font-medium uppercase tracking-wide text-muted-foreground">
              {dayLabel(invoice.invoice_date, formatDate)}
            </div>
          )}
          <div
            role="row"
            tabIndex={0}
            aria-selected={selectedId === invoice.id}
            onClick={(e) => { if (!(e.target as HTMLElement).closest("button, a, [role=menuitem]")) onView(invoice); }}
            onKeyDown={(e) => { if (e.target === e.currentTarget && (e.key === "Enter" || e.key === " ")) { e.preventDefault(); onView(invoice); } }}
            className={cn(
              grid,
              "flex flex-wrap items-center gap-x-3 gap-y-1 px-3 py-2 lg:py-0 lg:h-10 border-b border-divider last:border-b-0 cursor-pointer transition-colors",
              "hover:bg-muted/50 focus-visible:outline-none focus-visible:bg-muted/60",
              selectedId === invoice.id && "bg-primary/5"
            )}
          >
            {hidePatient ? (
              <div role="cell" className="min-w-0 flex-1 basis-full lg:basis-auto flex items-baseline gap-2">
                <span className={cn("text-sm font-mono", cancelled ? "text-muted-foreground" : "text-foreground")}>{invoice.invoice_number}</span>
                {invoice.doctor_name && <span className="truncate text-xs text-muted-foreground lg:hidden xl:inline">{invoice.doctor_name}</span>}
              </div>
            ) : (
              <>
                <div role="cell" className="min-w-0 flex-1 basis-full lg:basis-auto flex items-baseline gap-2">
                  <span className={cn("truncate text-sm font-medium", cancelled ? "text-muted-foreground" : "text-foreground")}>
                    {invoice.patient_name || "—"}
                  </span>
                  {/* Landscape tablets: the patient's name gets the narrow column; the doctor shows from xl */}
                  {invoice.doctor_name && <span className="truncate text-xs text-muted-foreground lg:hidden xl:inline">{invoice.doctor_name}</span>}
                </div>
                <div role="cell" className="hidden sm:block text-xs font-mono text-foreground/80 truncate">{invoice.invoice_number}</div>
              </>
            )}
            <div role="cell" className="text-xs text-muted-foreground tabular-nums whitespace-nowrap">{formatDate(invoice.invoice_date)}</div>
            <div role="cell" className="flex items-center gap-1.5 min-w-0">
              <StatusChip tone={status.tone}>{status.label}</StatusChip>
              {/* How it was paid — "I paid by UPI" is the question at the desk */}
              {!cancelled && paidVia(invoice) && <span className="truncate text-2xs text-muted-foreground">{paidVia(invoice)}</span>}
            </div>
            <div role="cell" className={cn("ml-auto lg:ml-0 text-right text-sm font-mono tabular-nums", cancelled ? "text-muted-foreground line-through decoration-1" : "text-foreground")}>
              {formatCurrency(Number(invoice.total_amount) || 0)}
            </div>
            <div role="cell" className={cn("text-right text-sm font-mono tabular-nums", !cancelled && due > 0 ? "text-highlight-foreground font-medium" : "hidden lg:block text-muted-foreground")}>
              <span className="lg:hidden text-2xs font-sans text-muted-foreground mr-1">Due</span>
              {!cancelled && due > 0 ? formatCurrency(due) : "—"}
            </div>

            <div role="cell" className="flex items-center justify-end gap-1 basis-full md:basis-auto">
              {onApplyAdvance && !cancelled && due > 0 && (
                <Button variant="outline" size="sm" onClick={() => onApplyAdvance(invoice)} className="h-8 px-2.5 text-xs gap-1.5" title="Pay this bill from the advance balance">
                  <Wallet className="w-3.5 h-3.5" /> Use advance
                </Button>
              )}
              {canPay && (
                <Button size="sm" onClick={() => onPayment!(invoice)} className="h-8 px-2.5 text-xs">
                  Collect
                </Button>
              )}
              <Button variant="ghost" size="sm" onClick={() => onPrint(invoice)} className="h-8 w-8 p-0" aria-label={`Print ${invoice.invoice_number}`} title="Print">
                <Printer className="w-4 h-4" />
              </Button>
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="ghost" size="sm" className="h-8 w-8 p-0" aria-label={`More actions for ${invoice.invoice_number}`}>
                    <MoreHorizontal className="w-4 h-4" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-48">
                  {canEdit && (
                    <DropdownMenuItem onSelect={() => onEdit!(invoice)}><Edit className="w-4 h-4 mr-2" /> Edit invoice</DropdownMenuItem>
                  )}
                  <DropdownMenuItem onSelect={() => shareOnWhatsApp(invoice, formatDate)}><Share2 className="w-4 h-4 mr-2" /> Share on WhatsApp</DropdownMenuItem>
                  {onViewPatient && invoice.patient_id && (
                    <DropdownMenuItem onSelect={() => onViewPatient(invoice.patient_id!)}><User className="w-4 h-4 mr-2" /> Patient's bills</DropdownMenuItem>
                  )}
                  {onCancel && !cancelled && (
                    <>
                      <DropdownMenuSeparator />
                      <DropdownMenuItem onSelect={() => onCancel(invoice)} className="text-destructive focus:text-destructive">
                        <XCircle className="w-4 h-4 mr-2" /> Cancel invoice
                      </DropdownMenuItem>
                    </>
                  )}
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          </div>
          </React.Fragment>
        );
      })}
    </div>
  );
}
